import React from 'react';
import { observer } from 'mobx-react';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import PodcastEpisodeCreate from 'pages/creator/podcast_episodes/podcast_episode_create';
import { PodcastEpisodeStore, PodcastStore } from '@seedlang/stores';
import Paginator from 'components/paginator';
import DeleteButton from 'components/button/delete_button';
import autobind from 'autobind-decorator';
import { Link } from 'react-router';
import InPlaceText from 'components/form/in_place_text';
import ReactTooltip from 'react-tooltip';
import { isPresent, isBlank } from '@seedlang/utils';
import { ExerciseUI } from '@seedlang/state';
import Checkbox from 'components/checkbox';
import { observable } from 'mobx';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';
import Spinner from 'components/spinner';
import { xor } from 'lodash';
import Filter from 'components/backend_filter/filter';
import Stack from 'components/layout/stack';
import SortableColumns from 'components/hoc/sortable_columns';
import cx from 'classnames';
import PageTitle from 'components/common/page_title';

const Wrapper = styled.div`
`;

const TableContainer = styled.div`
  position: relative;
  opacity: ${props => props.isLoading ? 0.6 : 1};
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`;

@observer
class PodcastEpisodeIndex extends React.Component {

  @observable selectedEpisodeIds = [];
  @observable allEpisodesSelected = false;
  @observable createPostSpinner = false;

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    if (!PodcastStore.hasIndexData) {
      PodcastStore.getIndex();
    }
  }

  @autobind onDelete(id) {
    PodcastEpisodeStore.destroy({ids: {podcastEpisodeId: id}}, this.getPodcastEpisodes);
  }

  @autobind getPodcastEpisodes() {
    PodcastEpisodeStore.reloadIndexFiltered({queryStrings: {include_count: true}});
  }

  @autobind onClickWorksheet(podcastEpisode) {
    if (isPresent(podcastEpisode.worksheetId)) {
      ExerciseUI.routeStore.routeToNamed('creator.worksheets.edit', { worksheetId: podcastEpisode.worksheetId});
    } else if (isPresent(podcastEpisode)) {
      this.createPostSpinner = podcastEpisode.id;
      ExerciseUI.worksheetStore.create({data: {podcast_episode_id: podcastEpisode.id}}, this.afterCreateWorksheet);
    }
  }

  @autobind afterCreateWorksheet(resp) {
    ExerciseUI.routeStore.routeToNamed('creator.worksheets.edit', { worksheetId: resp.id});
  }

  @autobind onSelectEpisode(episode) {
    this.selectedEpisodeIds = xor(this.selectedEpisodeIds, [episode.id]);
  }

  @autobind selectAllEpisodes() {
    if (this.allEpisodesSelected) {
      this.allEpisodesSelected = false;
      this.selectedEpisodeIds = [];
    } else {
      this.allEpisodesSelected = true;
      PodcastEpisodeStore.indexData.forEach(item => {
        if (!this.selectedEpisodeIds.includes(item.id)) {
          this.selectedEpisodeIds.push(item.id);
        }
      });
    }
  }

  @autobind destroySelectedEpisodes() {
    PodcastEpisodeStore.batchDelete({data: {ids: this.selectedEpisodeIds}}, this.afterDestroySelectedEpisodes);
  }

  @autobind afterDestroySelectedEpisodes() {
    this.selectedEpisodeIds = [];
    this.getPodcastEpisodes();
  }

  render() {
    return (
      <Wrapper>
        <PageTitle>Podcast Episodes</PageTitle>
        <Stack gap='md' alignItems='stretch'>
          <PodcastEpisodeCreate
            afterCreate={this.getPodcastEpisodes}
          />
          <Filter
            store={PodcastEpisodeStore}
            queryStrings={{include_count: true}}
            filters={[
              {
                type: 'text',
                name: 'podcast_episodes.title',
                label: 'Title',
                placeholder: 'Enter Title',
                default: '',
              },
              {
                type: 'select',
                name: 'podcast_id',
                options: PodcastStore.indexData.map(item => ({id: item.id, name: item.title})),
                labelField: 'name',
                placeholder: 'Select Podcast',
                label: 'Podcast',
                default: '',
              },
            ]}
          />
          <TableContainer isLoading={PodcastEpisodeStore.hasOutstandingRequests}>
            {PodcastEpisodeStore.hasOutstandingRequests && (
              <SpinnerOverlay>
                <Spinner />
              </SpinnerOverlay>
            )}
            <table className='table-wrapper admin-table'>
              <thead>
                <tr>
                  <th width='20' >
                    <Checkbox
                      value={this.allEpisodesSelected}
                      onClick={this.selectAllEpisodes}
                    />
                  </th>
                  <th width='20' />
                  <th
                    className={cx('sortable', this.props.columnClassNames('published_at'))}
                    onClick={() => this.props.onSortColumn('published_at')}
                  >
                    Published
                  </th>
                  <th>Title</th>
                  <th>Podcast</th>
                  <th>Post</th>
                  <th width='40'></th>
                </tr>
              </thead>
              <tbody>
                {
                  PodcastEpisodeStore.indexData.map(item => {
                return (
                  <tr key={item.id}>
                    <td>
                      <Checkbox
                        value={this.selectedEpisodeIds.includes(item.id)}
                        onClick={() => this.onSelectEpisode(item)}
                      />
                    </td>
                    <td>
                      <Link
                        to={{ name: 'creator.podcast_episodes.edit', params: { podcastEpisodeId: item.id } }}
                        className='on-click'
                      >
                        <i className='fa fa-chevron-right fa-block' />
                      </Link>
                    </td>
                    <td>
                      <div className='text'>
                        {item.publishedAt?.formattedDateWithFullYear}
                      </div>
                    </td>
                    <td>
                      <div className='text'>
                        <InPlaceText
                          field='title'
                          defaultValue={item.title}
                          model='podcast_episodes'
                          id={item.id}
                        />
                      </div>
                    </td>
                    <td>
                      <div className='text'>
                        {item.podcast?.title}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{display: 'flex', justifyContent: 'center', cursor: 'pointer'}}
                        data-tip
                        data-for={`worksheet-${item.id}`}
                        onClick={() => this.onClickWorksheet(item)}
                      >
                        {
                          isPresent(item.worksheetId) && this.createPostSpinner !== item.id &&
                            <i style={{fontSize: 18}} className='fa fa-check-circle' />
                        }
                        {
                          !isPresent(item.worksheetId) && this.createPostSpinner !== item.id &&
                            <i style={{fontSize: 18, color: 'gray'}} className='fa fa-plus-circle' />
                        }
                        {
                          this.createPostSpinner === item.id &&
                            <Spinner />
                        }
                        <ReactTooltip
                          place='left'
                          type='dark'
                          effect='solid'
                          id={`worksheet-${item.id}`}
                          class='custom-tooltip'
                        >
                          {isPresent(item.worksheetId) ? 'Go to Post' : 'Create Post'}
                        </ReactTooltip>
                      </div>
                    </td>
                    <td>
                      <DeleteButton
                        onConfirm={() => this.onDelete(item.id)}
                        message='Delete this grid item?'
                        className='fa-block right'
                      />
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
            </table>
          </TableContainer>
          <Paginator
            store={PodcastEpisodeStore}
            onPagination={() => this.allEpisodesSelected = false}
          />
          {
            PodcastEpisodeStore.hasIndexData &&
              <DeleteButton
                confirmationMessageFloatsRight
                top='-12px'
                left='200px'
                right='auto'
                message={`${this.selectedEpisodeIds.length} podcast episodes will be deleted`}
                disabled={isBlank(this.selectedEpisodeIds)}
                onConfirm={this.destroySelectedEpisodes}
              >
                <Button
                  background={Theme.red}
                  margin='10px 0 0 0'
                  disabled={isBlank(this.selectedEpisodeIds)}
                >
                  <i className='fa fa-trash'/>
                  {`Delete ${this.selectedEpisodeIds.length} episodes`}
                </Button>
              </DeleteButton>
          }
        </Stack>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(SortableColumns(PodcastEpisodeIndex, PodcastEpisodeStore, {
  sortColumn: 'published_at',
  sortIsAscending: false,
}));
